import React from "react";
import { AbsoluteFill, Audio, Sequence, staticFile, useCurrentFrame, useVideoConfig } from "remotion";
import { fadeIn, slideUp, scaleIn, progressBar, fillBar, countUp } from "../utils/animations";

export interface Pick {
  game: string;
  pick: string;
  odds: string;
  confidence: number; // 0-100
  result?: "W" | "L" | "push" | null;
  sport?: string;
}

export interface StatsVideoProps {
  picks: Pick[];
  recordStat?: string;   // e.g. "23-7"
  winRate?: number;      // e.g. 77 (percent)
  winRateLabel?: string;
  headline?: string;
  brandName?: string;
  accentColor?: string;
  voiceFile?: string;
}

const ac = (p: StatsVideoProps) => p.accentColor ?? "#38bdf8";

// ── Record Splash ─────────────────────────────────────────────────────────────

const RecordScene: React.FC<{ p: StatsVideoProps }> = ({ p }) => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();
  const isVertical = height > width;
  const accent = ac(p);
  const winPct = countUp(frame, 10, 55, p.winRate ?? 0);

  return (
    <AbsoluteFill style={{ background: "linear-gradient(160deg, #020617 0%, #0a1628 60%, #020617 100%)", fontFamily: "'Inter', system-ui, sans-serif", overflow: "hidden", justifyContent: "center", alignItems: "center", flexDirection: "column", padding: 60 }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: accent, boxShadow: `0 0 24px ${accent}` }} />
      <div style={{ position: "absolute", top: "35%", left: "50%", transform: "translate(-50%,-50%)", width: 700, height: 600, background: `radial-gradient(ellipse, ${accent}15 0%, transparent 65%)` }} />

      {/* Brand */}
      <div style={{ opacity: fadeIn(frame, 5), fontSize: isVertical ? 28 : 22, color: accent, fontWeight: 900, letterSpacing: 3, textTransform: "uppercase", marginBottom: 24 }}>
        {p.brandName ?? "POWERPICKS"}
      </div>

      {/* Win rate counter */}
      {p.winRate !== undefined && (
        <div style={{ opacity: fadeIn(frame, 8), transform: `scale(${scaleIn(frame, 8)})`, textAlign: "center", marginBottom: 28 }}>
          <div style={{ fontSize: isVertical ? 140 : 100, fontWeight: 900, color: accent, lineHeight: 1, textShadow: `0 0 80px ${accent}66` }}>
            {winPct}%
          </div>
          <div style={{ fontSize: isVertical ? 30 : 22, color: "#64748b", marginTop: 8, fontWeight: 500 }}>
            {p.winRateLabel ?? "win rate"}
          </div>
        </div>
      )}

      {/* Record */}
      {p.recordStat && (
        <div style={{ opacity: fadeIn(frame, 20), display: "flex", alignItems: "center", gap: 20, background: `${accent}12`, border: `1px solid ${accent}33`, borderRadius: 16, padding: isVertical ? "20px 44px" : "16px 36px", marginBottom: 24 }}>
          <div style={{ fontSize: isVertical ? 52 : 38, fontWeight: 900, color: "#f1f5f9" }}>{p.recordStat}</div>
          <div style={{ fontSize: isVertical ? 26 : 20, color: "#475569" }}>record</div>
        </div>
      )}

      {p.headline && (
        <div style={{ opacity: fadeIn(frame, 30), transform: `translateY(${slideUp(frame, 30, 18, 20)}px)`, fontSize: isVertical ? 44 : 32, color: "#94a3b8", textAlign: "center", lineHeight: 1.5, maxWidth: "80%" }}>
          {p.headline}
        </div>
      )}

      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 4, background: "#1e3a5f" }}>
        <div style={{ height: "100%", width: `${progressBar(frame, 90)}%`, background: accent }} />
      </div>
    </AbsoluteFill>
  );
};

// ── Pick Card Scene ───────────────────────────────────────────────────────────

const PickCardScene: React.FC<{ p: StatsVideoProps; pick: Pick; index: number; total: number }> = ({ p, pick, index, total }) => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();
  const isVertical = height > width;
  const accent = ac(p);
  const confWidth = fillBar(frame, 20, 40, pick.confidence);

  const resultColor = pick.result === "W" ? "#22c55e" : pick.result === "L" ? "#ef4444" : pick.result === "push" ? "#f59e0b" : "#64748b";
  const resultLabel = pick.result === "W" ? "WIN ✓" : pick.result === "L" ? "LOSS ✗" : pick.result === "push" ? "PUSH" : "PENDING";

  return (
    <AbsoluteFill style={{ background: "linear-gradient(160deg, #020617 0%, #060f1e 60%, #020617 100%)", fontFamily: "'Inter', system-ui, sans-serif", overflow: "hidden", justifyContent: "center", alignItems: "center", flexDirection: "column", padding: 60 }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: accent }} />

      {/* Pick counter */}
      <div style={{ opacity: fadeIn(frame, 5), fontSize: isVertical ? 24 : 18, color: "#475569", fontWeight: 600, letterSpacing: 2, marginBottom: 16 }}>
        PICK {index + 1} OF {total}  {pick.sport ? `· ${pick.sport.toUpperCase()}` : ""}
      </div>

      {/* Main card */}
      <div style={{ opacity: fadeIn(frame, 10), transform: `translateY(${slideUp(frame, 10)}px)`, width: "100%", background: `${accent}0d`, border: `2px solid ${accent}33`, borderRadius: 24, padding: isVertical ? "44px 48px" : "32px 40px", marginBottom: 28 }}>
        {/* Game */}
        <div style={{ fontSize: isVertical ? 30 : 22, color: "#475569", fontWeight: 600, marginBottom: 12, letterSpacing: 1 }}>
          {pick.game}
        </div>
        {/* Pick */}
        <div style={{ fontSize: isVertical ? 68 : 48, fontWeight: 900, color: "#f1f5f9", marginBottom: 16, lineHeight: 1.1 }}>
          {pick.pick}
        </div>
        {/* Odds + Result row */}
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 28 }}>
          <div style={{ background: `${accent}22`, border: `1px solid ${accent}55`, borderRadius: 8, padding: "6px 20px", fontSize: isVertical ? 32 : 24, fontWeight: 700, color: accent }}>
            {pick.odds}
          </div>
          {pick.result && (
            <div style={{ background: `${resultColor}22`, border: `1px solid ${resultColor}55`, borderRadius: 8, padding: "6px 20px", fontSize: isVertical ? 28 : 20, fontWeight: 700, color: resultColor }}>
              {resultLabel}
            </div>
          )}
        </div>
        {/* Confidence bar */}
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: isVertical ? 24 : 18, color: "#475569", fontWeight: 600 }}>
            <span>Confidence</span>
            <span style={{ color: accent, fontWeight: 700 }}>{pick.confidence}%</span>
          </div>
          <div style={{ height: isVertical ? 12 : 8, background: "#1e3a5f", borderRadius: 6, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${confWidth}%`, background: `linear-gradient(90deg, ${accent}cc, ${accent})`, borderRadius: 6, boxShadow: `0 0 10px ${accent}88` }} />
          </div>
        </div>
      </div>

      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 4, background: "#1e3a5f" }}>
        <div style={{ height: "100%", width: `${progressBar(frame, 90)}%`, background: accent }} />
      </div>
    </AbsoluteFill>
  );
};

// ── Summary Scene ─────────────────────────────────────────────────────────────

const SummaryScene: React.FC<{ p: StatsVideoProps }> = ({ p }) => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();
  const isVertical = height > width;
  const accent = ac(p);

  const wins = p.picks.filter(pk => pk.result === "W").length;
  const losses = p.picks.filter(pk => pk.result === "L").length;
  const pending = p.picks.filter(pk => !pk.result).length;

  return (
    <AbsoluteFill style={{ background: "#020617", fontFamily: "'Inter', system-ui, sans-serif", overflow: "hidden", justifyContent: "center", alignItems: "center", flexDirection: "column", padding: 60 }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: accent, boxShadow: `0 0 20px ${accent}` }} />
      <div style={{ position: "absolute", top: "40%", left: "50%", transform: "translate(-50%,-50%)", width: 700, height: 500, background: `radial-gradient(ellipse, ${accent}12 0%, transparent 65%)` }} />

      <div style={{ opacity: fadeIn(frame, 5), fontSize: isVertical ? 32 : 24, color: accent, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase", marginBottom: 32 }}>
        today's picks
      </div>

      {/* Stats row */}
      <div style={{ opacity: fadeIn(frame, 12), display: "flex", gap: 20, marginBottom: 40 }}>
        {[
          { label: "Picks", val: p.picks.length, color: accent },
          ...(wins > 0 ? [{ label: "Wins", val: wins, color: "#22c55e" }] : []),
          ...(losses > 0 ? [{ label: "Losses", val: losses, color: "#ef4444" }] : []),
          ...(pending > 0 ? [{ label: "Live", val: pending, color: "#f59e0b" }] : []),
        ].map((s, i) => (
          <div key={i} style={{ textAlign: "center", background: `${s.color}12`, border: `1px solid ${s.color}33`, borderRadius: 16, padding: isVertical ? "24px 32px" : "18px 28px" }}>
            <div style={{ fontSize: isVertical ? 72 : 52, fontWeight: 900, color: s.color, lineHeight: 1 }}>{s.val}</div>
            <div style={{ fontSize: isVertical ? 24 : 18, color: "#475569", marginTop: 6 }}>{s.label}</div>
          </div>
        ))}
      </div>

      <div style={{ opacity: fadeIn(frame, 28), transform: `scale(${scaleIn(frame, 28)})`, background: accent, color: "#020617", padding: isVertical ? "20px 52px" : "14px 40px", borderRadius: 50, fontSize: isVertical ? 36 : 26, fontWeight: 900, boxShadow: `0 0 40px ${accent}66` }}>
        Join {p.brandName ?? "PowerPicks"} →
      </div>

      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 4, background: "#1e3a5f" }}>
        <div style={{ height: "100%", width: `${progressBar(frame, 90)}%`, background: accent }} />
      </div>
    </AbsoluteFill>
  );
};

// ── StatsVideo ────────────────────────────────────────────────────────────────

export const StatsVideo: React.FC<StatsVideoProps> = (props) => {
  const picksToShow = props.picks.slice(0, 4);
  let offset = 90; // after record scene

  return (
    <AbsoluteFill>
      <Sequence from={0} durationInFrames={90}><RecordScene p={props} /></Sequence>
      {picksToShow.map((pick, i) => {
        const start = offset;
        offset += 90;
        return (
          <Sequence key={i} from={start} durationInFrames={90}>
            <PickCardScene p={props} pick={pick} index={i} total={picksToShow.length} />
          </Sequence>
        );
      })}
      <Sequence from={offset} durationInFrames={90}><SummaryScene p={props} /></Sequence>
      {props.voiceFile && <Audio src={staticFile(props.voiceFile)} />}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
